/**
 * Shares API Service
 *
 * API client functions for reflection share link management.
 * Owner endpoints use authenticated apiClient; viewer endpoints use
 * plain axios (no auth required).
 */

import axios from "axios";
import { apiClient } from "./api";
import type {
  ShareResponse,
  ShareCreateRequest,
  ShareInfoResponse,
  ShareContentResponse,
} from "@/types/share";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";
const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";

// Unauthenticated client for viewer endpoints
const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// ==================== Owner endpoints (authenticated) ====================

export async function createShare(
  request: ShareCreateRequest
): Promise<ShareResponse> {
  const response = await apiClient.post(`${API_PREFIX}/shares`, request);
  return response.data;
}

export async function listShares(
  reflectionId: string
): Promise<ShareResponse[]> {
  const response = await apiClient.get(`${API_PREFIX}/shares`, {
    params: { reflection_id: reflectionId },
  });
  return response.data;
}

export async function getShare(shareId: string): Promise<ShareResponse> {
  const response = await apiClient.get(`${API_PREFIX}/shares/${shareId}`);
  return response.data;
}

export async function revokeShare(shareId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/shares/${shareId}`);
}

// ==================== Viewer endpoints (public, no auth) ====================

export async function getShareInfo(token: string): Promise<ShareInfoResponse> {
  const response = await publicClient.get(`${API_PREFIX}/shares/${token}/info`);
  return response.data;
}

export async function requestShareAccess(
  token: string,
  email: string
): Promise<{ message: string }> {
  const response = await publicClient.post(
    `${API_PREFIX}/shares/${token}/request-access`,
    { email }
  );
  return response.data;
}

export async function verifySharePin(
  token: string,
  email: string,
  pin: string
): Promise<ShareContentResponse> {
  const response = await publicClient.post(
    `${API_PREFIX}/shares/${token}/verify`,
    { email, pin }
  );
  return response.data;
}
